import {
  BaseEdge,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
} from "@xyflow/react";

/**
 * How far past each handle the curve's tangent reaches. The Figma card gap is
 * 28px, so a bezier whose control points sit at the gap's midpoint (React
 * Flow's default) collapses into a near-vertical line with hooked ends, and a
 * step path has no room for a corner radius — it renders as a square bracket.
 * A wider tangent lets the line leave and enter horizontally and cross the
 * gap in one swoop. Between cards on the same line the control points fall on
 * the line itself, so the path is straight.
 */
const MIN_TANGENT_PX = 36;

export const ruleGraphFlowEdgePath = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}): string => {
  const tangent = Math.max(Math.abs(targetX - sourceX) / 2, MIN_TANGENT_PX);
  return `M ${sourceX},${sourceY} C ${sourceX + tangent},${sourceY} ${
    targetX - tangent
  },${targetY} ${targetX},${targetY}`;
};

/**
 * A flow edge between pipeline cards: a cubic curve with horizontal tangents
 * at both handles. Shared tail steps (the platform exclusions card, the
 * output) sit vertically centered across the rule rows, so several rows fan
 * into one card through the same 28px gap; the curves converge tangentially
 * at the target instead of stacking square brackets on top of each other.
 */
const RuleGraphFlowEdgeLine = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  style,
}: EdgeProps) => (
  <BaseEdge
    id={id}
    markerEnd={markerEnd}
    path={ruleGraphFlowEdgePath({ sourceX, sourceY, targetX, targetY })}
    style={style}
  />
);

/** Restore smooth flow curves where clear; retain ELK's safe corridor elsewhere. */
export function ruleGraphRoutedEdgePath({
  path,
  allowCurve = false,
  obstacles = [],
}: {
  path: string;
  allowCurve?: boolean;
  obstacles?: { x: number; y: number; width: number; height: number }[];
}): string {
  const points: { x: number; y: number }[] = [];
  for (const match of path.matchAll(/[ML] ([\d.e+-]+),([\d.e+-]+)/g)) {
    const point = { x: Number(match[1]), y: Number(match[2]) };
    const last = points.at(-1);
    if (last?.x === point.x && last.y === point.y) continue;
    const previous = points.at(-2);
    if (previous && last) {
      const ax = last.x - previous.x,
        ay = last.y - previous.y;
      const bx = point.x - last.x,
        by = point.y - last.y;
      if (ax * by === ay * bx && ax * bx + ay * by >= 0) points.pop();
    }
    points.push(point);
  }
  const first = points[0],
    last = points.at(-1);
  if (!first || !last) return "";
  if (allowCurve && last.x > first.x) {
    const tangent = Math.max((last.x - first.x) / 2, MIN_TANGENT_PX);
    const left = Math.min(first.x, last.x - tangent);
    const right = Math.max(last.x, first.x + tangent);
    const top = Math.min(first.y, last.y),
      bottom = Math.max(first.y, last.y);
    const blocked = obstacles.some(
      (rect) =>
        rect.x < right &&
        rect.x + rect.width > left &&
        rect.y < bottom &&
        rect.y + rect.height > top
    );
    if (!blocked)
      return ruleGraphFlowEdgePath({
        sourceX: first.x,
        sourceY: first.y,
        targetX: last.x,
        targetY: last.y,
      });
  }
  let result = `M ${first.x},${first.y}`;
  for (let index = 1; index < points.length - 1; index++) {
    const before = points[index - 1]!,
      corner = points[index]!,
      after = points[index + 1]!;
    const incoming = Math.hypot(corner.x - before.x, corner.y - before.y);
    const outgoing = Math.hypot(after.x - corner.x, after.y - corner.y);
    const cross =
      (corner.x - before.x) * (after.y - corner.y) -
      (corner.y - before.y) * (after.x - corner.x);
    if (cross === 0) {
      result += ` L ${corner.x},${corner.y}`;
      continue;
    }
    const radius = Math.min(12, incoming / 2, outgoing / 2);
    const start = {
      x: corner.x + ((before.x - corner.x) * radius) / incoming,
      y: corner.y + ((before.y - corner.y) * radius) / incoming,
    };
    const end = {
      x: corner.x + ((after.x - corner.x) * radius) / outgoing,
      y: corner.y + ((after.y - corner.y) * radius) / outgoing,
    };
    result += ` L ${start.x},${start.y} Q ${corner.x},${corner.y} ${end.x},${end.y}`;
  }
  return points.length === 1 ? result : `${result} L ${last.x},${last.y}`;
}
const RuleGraphRoutedEdge = ({
  id,
  data,
  markerEnd,
  style,
}: EdgeProps<Edge<{ path: string }>>) => (
  <BaseEdge
    id={id}
    path={data?.path ?? ""}
    markerEnd={markerEnd}
    style={style}
  />
);

export const RULE_GRAPH_EDGE_TYPES: EdgeTypes = {
  ruleGraphFlow: RuleGraphFlowEdgeLine,
  ruleGraphRouted: RuleGraphRoutedEdge,
};
